import { useState, useEffect, useMemo } from 'react';
import { mediaService } from '../services/media.service';
import type { MediaDto } from '../types/media.dto';

export function extractFileKey(avatar: string): string | null {
  if (!avatar) return null;
  // If it's already a key (no slashes), return avatar;
  if (!avatar.includes('/')) return avatar;
  // If it's a URL, extract the key after the last '/'
  try {
    const url = new URL(avatar);
    return url.pathname.split('/').pop() || null;
  } catch {
    // fallback: try splitting string
    return avatar.split('/').pop() || null;
  }
}

export function useMedia() {
  // ...existing state and logic
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mediaFilesState, setMediaFiles] = useState<MediaDto[]>([]);
  // Memoize mediaFiles to prevent unnecessary effect runs
  const mediaFiles = useMemo(() => mediaFilesState, [mediaFilesState]);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  // Generate/correct preview URLs for images
  // This effect is moved from MediaUpload
  useEffect(() => {
    // This hook now requires a memberId to be passed in for preview generation
    // You should pass the memberId of the selected member
    // Example: useMedia(selectedMember?.id)
    // Below, we assume memberId is available in closure or via hook argument
    // For now, add memberId as a parameter to the hook (breaking change if needed)
    // For this patch, use a placeholder and add a comment
    const memberId = undefined as any; // TODO: Pass memberId from component using this hook
    mediaFiles.forEach(async (file) => {
      const isImage = /^image\/(jpeg|png|gif|webp|jpg)$/i.test(file.mimeType);
      if (!isImage) return;
      if (file.thumbnail || file.url) {
        // Try loading thumbnail or url as image; if it fails, fallback to blob
        const img = new window.Image();
        img.src = file.thumbnail || file.url;
        img.onload = () => {
          setPreviewUrls((prev) => ({ ...prev, [file.fileKey]: file.thumbnail || file.url }));
        };
        img.onerror = async () => {
          try {
            const blob = await mediaService.download(memberId, file.fileKey);
            const objectUrl = URL.createObjectURL(blob);
            setPreviewUrls((prev) => ({ ...prev, [file.fileKey]: objectUrl }));
          } catch (e: any) {
            setPreviewUrls((prev) => ({ ...prev, [file.fileKey]: '' }));
            setError(`Failed to generate preview for ${file.fileKey}: ${e?.message || 'Unknown error'}`);
          }
        };
      } else {
        // No url, fetch blob
        try {
          const blob = await mediaService.download(memberId, file.fileKey);
          const objectUrl = URL.createObjectURL(blob);
          setPreviewUrls((prev) => ({ ...prev, [file.fileKey]: objectUrl }));
        } catch (e: any) {
          setPreviewUrls((prev) => ({ ...prev, [file.fileKey]: '' }));
          setError(`Failed to generate preview for ${file.fileKey}: ${e?.message || 'Unknown error'}`);
        }
      }
    });
    // Clean up object URLs on unmount or file removal
    return () => {
      Object.values(previewUrls).forEach((url) => {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFiles]);

  const upload = async (file: File, memberId: string): Promise<MediaDto> => {
    setUploading(true);
    setError(null);
    try {
      const res = await mediaService.upload(file, memberId);
      setMediaFiles((prev) => [...prev, res]);
      return res;
    } catch (e: any) {
      setError(e.message || 'Upload failed');
      throw e;
    } finally {
      setUploading(false);
    }
  };

  // Helper to get a signed S3 URL for download/share
  const getSignedMediaUrl = async (memberId: string, fileKey: string) => {
    if (!fileKey) return null;
    try {
      const signedUrl = await mediaService.getSignedUrl(memberId, fileKey);
      return signedUrl;
    } catch (e) {
      setError('Could not get download URL');
      return null;
    }
  };

  const remove = async (fileKey: string) => {
    try {
      await mediaService.deleteMedia(fileKey);
      setMediaFiles((prev) => prev.filter((m) => m.fileKey !== fileKey));
    } catch (e: any) {
      setError(e.message || 'Delete failed');
      throw e;
    }
  };

  return {
    // ...existing returns,
    getSignedMediaUrl, upload, uploading, error, mediaFiles, setMediaFiles, remove, previewUrls };
}
